<template>
    <div>
        <OnboardingWrapper>
        <div v-if="!isLoading">
            <div id="game-detail-testers">

                <!-- ── Toast: Tester Added Confirmation ── -->
                <transition name="toast-fade">
                    <div v-if="showAddedToast" class="added-toast">
                        <span class="added-toast-icon">✓</span>
                        <span>Tester added successfully!</span>
                    </div>
                </transition>

                <!-- ── View 0: Testers Dashboard (merged list + find/add) ── -->
                <div v-if="current == 0" class="testers-view0">
                    <div id="title-wrapper">
                        <gameTitle :id="detail.gameName" :title="detail.gameName" :img="detail.img" :company="detail.company"
                            :endTime="detail.endTime" />
                    </div>

                    <div class="testers-card">

                        <!-- Assigned testers -->
                        <div class="testers-card-section testers-panel-yours">
                            <div class="testers-card-header">
                                <h6 class="testers-card-title">Your Testers</h6>
                                <span class="testers-count-badge">{{ latestTesterIDs.length }}</span>
                            </div>

                            <div class="testers-panel-scroll">
                                <div v-if="latestTesterIDs.length" class="tester-list">
                                    <gameTester
                                        v-for="testerId in latestTesterIDs"
                                        :key="testerId"
                                        :id="testerId"
                                        :initial-name="optimisticTesterData[testerId] && optimisticTesterData[testerId].name"
                                        :initial-email="optimisticTesterData[testerId] && optimisticTesterData[testerId].email"
                                        :initial-img="optimisticTesterData[testerId] && optimisticTesterData[testerId].pic"
                                        :initial-achievements="optimisticTesterData[testerId] && optimisticTesterData[testerId].achievementsCompleted"
                                        @goResponse="handleGoResponse"
                                        @editTimeSlot="handleEditTimeSlot"
                                        @removeTester="handleRemoveTester"
                                        @message-tester="handleMessageTester"
                                    />
                                </div>
                                <div v-else class="testers-empty-hint">
                                    No testers assigned yet — find some below and hit Add.
                                </div>
                            </div>
                        </div>

                        <div class="testers-card-divider"></div>

                        <!-- Find & add testers -->
                        <div class="testers-card-section testers-panel-find">
                            <div class="testers-card-header">
                                <h6 class="testers-card-title">Find More Testers</h6>
                                <span class="results-count">{{ searchFilteredList.length }} tester{{ searchFilteredList.length !== 1 ? 's' : '' }} found</span>
                            </div>

                            <div class="testers-panel-scroll">
                                <div class="find-testers-controls">
                                    <b-form-input
                                        v-model="searchTerm"
                                        placeholder="Search by name..."
                                        class="filter-search-input"
                                        @input="onSearchInput"
                                    ></b-form-input>

                                    <button class="filters-toggle-btn" @click="filtersOpen = !filtersOpen">
                                        Filters <span v-if="selectedTags.length" class="filters-count">({{ selectedTags.length }})</span>
                                        <span :class="['filters-caret', { open: filtersOpen }]">▲</span>
                                    </button>
                                </div>

                                <div v-if="selectedTags.length" class="selected-tags-wrapper">
                                    <div class="selected-tags-pills">
                                        <button
                                            v-for="tag in selectedTags"
                                            :key="tag.fieldValue"
                                            class="tag-button-chosen"
                                            @click="removeTag(tag)"
                                        >
                                            {{ tag.fieldValue }} ✕
                                        </button>
                                    </div>
                                    <button class="filter-clear-btn" @click="handleClear">Clear All</button>
                                </div>

                                <div v-if="filtersOpen" class="filters-drawer">
                                    <details
                                        v-for="(fieldObject, field_index) in testerFields"
                                        :key="field_index"
                                        class="tag-dropdown"
                                        open
                                    >
                                        <summary class="tag-category-header">{{ formatFieldName(fieldObject.fieldName) }}</summary>
                                        <div class="tag-options">
                                            <button
                                                v-for="(fieldValue, value_index) in fieldObject.fieldValues"
                                                :key="value_index"
                                                :class="['tag-btn', { selected: fieldValue.isAdded }]"
                                                @click="handleFieldValue(fieldValue.value, field_index)"
                                            >
                                                {{ fieldValue.value }}
                                            </button>
                                        </div>
                                    </details>
                                </div>

                                <div v-if="isLoadingTesters" class="loading-testers">
                                    Loading testers...
                                </div>

                                <div v-else-if="testerError" class="add-tester-no-tester">
                                    <div class="add-tester-no-tester-txt">{{ testerError }}</div>
                                </div>
                                <div v-else-if="displayedTesters.length === 0" class="add-tester-no-tester">
                                    <img src="../../assets/Not Found.png" class="add-tester-no-tester-img">
                                    <div class="add-tester-no-tester-txt">No testers match your filters (´；ω；`)</div>
                                </div>

                                <div v-else class="tester-cards-grid">
                                    <addTesterItem
                                        v-for="addItem in displayedTesters"
                                        :key="addItem.id"
                                        :id="addItem.id"
                                        :name="addItem.name"
                                        :email="addItem.email"
                                        :gameId="gameId"
                                        @addTester="handleAddTester"
                                    />
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="pagination-row">
                                    <button
                                        class="page-btn"
                                        :disabled="currentPage === 1"
                                        @click="currentPage--"
                                    >← Prev</button>
                                    <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
                                    <button
                                        class="page-btn"
                                        :disabled="currentPage === totalPages"
                                        @click="currentPage++"
                                    >Next →</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ── View 1: Tester Responses ── -->
                <div v-if="current == 1" id="game-detail-testers-response">
                    <div class="addtester-back" @click="toDetailTest">← Go Back To Tester List</div>
                    <gameTitle :id="detail.gameName" :title="detail.gameName" :img="detail.img" :company="detail.company"
                        :endTime="detail.endTime" />
                    <div v-if="responseList.length > 0" class="row">
                        <div class="col-12 mt-2">
                            <div class="row my-3">
                                <div class="col-12">
                                    <b-button variant="transparent" class="mr-2 all" @click="openAll">Open all</b-button>
                                    <b-button variant="transparent" class="all" @click="closeAll">Close all</b-button>
                                </div>
                            </div>
                            <div class="row mb-4" v-for="(collapse, index) in collapses" :key="index">
                                <div class="col-12">
                                    <b-button @click="collapse.show = !collapse.show"
                                        v-bind:class="{ 'active-style': collapse.show, 'hidden-style': !collapse.show }"
                                        variant="transparent" class="response-title">
                                        <b-icon icon="arrow-right" v-if="!collapse.show" aria-hidden="true"></b-icon>
                                        <b-icon icon="arrow-down" v-if="collapse.show" aria-hidden="true"></b-icon>
                                        Response {{ index + 1 }}
                                    </b-button>
                                    <b-collapse v-model="collapse.show" id="collapse-1" class="mt-2">
                                        <b-card class="response-detail">
                                            <div class="response-text" v-for="(entry, j) in responseList[index].questions" :key="j">
                                                <p class="card-text white-text">Question: {{ entry }}</p>
                                                <p class="card-text white-text">Answer: {{ responseList[index].answers[j] }}</p>
                                            </div>
                                        </b-card>
                                    </b-collapse>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="responseList.length == 0" class="no-response">
                        <img src="../../assets/Not Found.png" class="no-response-img">
                        <div class="no-response-txt">This tester has no response (´；ω；`)</div>
                    </div>
                </div>

                <!-- ── View 3: Assign Time Slot ── -->
                <div v-if="current == 3" id="game-detail-testers-assign">
                    <div class="addtester-back" @click="toDetailTest">← Go Back To Tester List</div>
                    <gameTitle :id="detail.gameName" :title="detail.gameName" :img="detail.img" :company="detail.company"
                        :endTime="detail.endTime" />
                    <div class="game-detail-testers-add-title">TESTER</div>
                    <addTesterItem :key="assignTesterId" :id="assignTesterId" :name="assigntesterInfo.userName"
                        :email="assigntesterInfo.email" :hasBorder=false :gameId="gameId" :hasButton=false
                        @addTester="handleAddTester" />
                    <div class="game-detail-testers-add-title">ASSIGNED TIME SLOTS</div>
                    <div class="one-slot" v-for="oneSlot in assigntesterTimeSlotList" :key="oneSlot">
                        {{ 'From ' + oneSlot[0].replace("T", " ") + ' to ' + oneSlot[1].replace("T", " ") }}
                    </div>
                    <div class="game-detail-testers-add-title">ADD NEW TIME SLOT</div>
                    <div class="game-detail-assign-ctn">
                        <div class="game-detail-assign-date-item">
                            Year:
                            <select class="game-detail-assign-selection-box" @change="handleSelect('year', $event)">
                                <option v-for="(year) in assignYearSelection" :key="year.yearText">{{ year.yearText }}</option>
                            </select>
                        </div>
                        <div class="game-detail-assign-date-item">
                            Month:
                            <select class="game-detail-assign-selection-box" @change="handleSelect('month', $event)">
                                <option v-for="(month) in assignMonthSelection" :key="month.monthText">{{ month.monthText }}</option>
                            </select>
                        </div>
                        <div class="game-detail-assign-date-item">
                            Day:
                            <select class="game-detail-assign-selection-box" @change="handleSelect('day', $event)">
                                <option v-for="(day) in assignDaySelection" :key="day.dayText">{{ day.dayText }}</option>
                            </select>
                        </div>
                        <div class="game-detail-assign-date-item">
                            Hour:
                            <select class="game-detail-assign-selection-box" @change="handleSelect('hour', $event)">
                                <option v-for="(hour) in assignHourSelection" :key="hour.timeText">{{ hour.timeText }}</option>
                            </select>
                        </div>
                        <div class="game-detail-assign-btn" @click.prevent="handleAdd">Add</div>
                    </div>
                </div>

            </div>
        </div>
        <Loading v-else />

        <MessagePanel ref="messagePanel" :contacts="messageContacts" :userType="this.userType" />
        </OnboardingWrapper>
    </div>
</template>

<script>
import Chat from '../../Firestore/Chat.js'
import MessagePanel from '../messagebox/developer-messagepanel.vue'
import gameTester from './GameTester.vue';
import gameTitle from './GameTitle.vue';
import addTesterItem from './AddTesterItem.vue';
import Loading from '../loading/index.vue'
import UserObject from "../../Firestore/UserObject";
import GamesObject from "../../Firestore/GamesObject";
import FilteringTesters from "../../Firestore/FilteringTesters";
import GameTesting from "../../Firestore/GameTesting";
import SurveyAnalytics from "../../Firestore/SurveyAnalytics";
import { ref } from 'vue';
import OnboardingWrapper from '../onboarding/OnboardingWrapper.vue';
import { onboardingService } from '@/services/OnboardingService';
import { initializePageOnboarding } from '@/services/onboardingConfigs';

const today = new Date();
const PAGE_SIZE = 12; // testers shown per page in the results panel

export default {
    name: "gameDetailTesters",
    components: { gameTester, gameTitle, addTesterItem, Loading, MessagePanel, OnboardingWrapper },
    props: { detail: { type: Object } },
    data() {
        return {
            messageContacts: [],
            // ── Filter state ──
            searchTerm: "",
            searchDebounce: null,
            selectedTags: [],       // [{ fieldValue, field_index }]
            testerFields: [],
            variantsList: [],
            filtersOpen: false,     // controls the collapsible filter drawer on the merged dashboard
            // ── All testers (fetched once per filter change, ALL pages) ──
            allTesterList: [],      // full filtered list from Firestore
            isLoadingTesters: false,
            testerError: "",
            // ── Pagination (client-side, over allTesterList) ──
            currentPage: 1,
            pageSize: PAGE_SIZE,
            // ── General ──
            latestTesterIDs: [],
            optimisticTesterData: {}, // testerId -> { name, email, pic, achievementsCompleted } for testers just added, so their card renders correctly right away instead of racing a fresh profile fetch
            userType: window.localStorage.getItem("userType") ? parseInt(window.localStorage.getItem("userType")) : -1,
            gameId: window.localStorage.getItem("gameId"),
            isLoading: true,
            current: 0,
            // ── Assign slot ──
            assignTesterId: "",
            assigntesterInfo: {},
            assigntesterTimeSlotList: [],
            assignYearSelected: "",
            assignMonthSelected: "",
            assignDaySelected: "",
            assignHourSelected: 0,
            assignYearSelection: [],
            assignDaySelection: [],
            assignHourSelection: [
                { timeText: "0:00 AM - 1:00 AM",   startTimeString: "00:00:00", endTimeString: "01:00:00" },
                { timeText: "1:00 AM - 2:00 AM",   startTimeString: "01:00:00", endTimeString: "02:00:00" },
                { timeText: "2:00 AM - 3:00 AM",   startTimeString: "02:00:00", endTimeString: "03:00:00" },
                { timeText: "3:00 AM - 4:00 AM",   startTimeString: "03:00:00", endTimeString: "04:00:00" },
                { timeText: "4:00 AM - 5:00 AM",   startTimeString: "04:00:00", endTimeString: "05:00:00" },
                { timeText: "5:00 AM - 6:00 AM",   startTimeString: "05:00:00", endTimeString: "06:00:00" },
                { timeText: "6:00 AM - 7:00 AM",   startTimeString: "06:00:00", endTimeString: "07:00:00" },
                { timeText: "7:00 AM - 8:00 AM",   startTimeString: "07:00:00", endTimeString: "08:00:00" },
                { timeText: "8:00 AM - 9:00 AM",   startTimeString: "08:00:00", endTimeString: "09:00:00" },
                { timeText: "9:00 AM - 10:00 AM",  startTimeString: "09:00:00", endTimeString: "10:00:00" },
                { timeText: "10:00 AM - 11:00 AM", startTimeString: "10:00:00", endTimeString: "11:00:00" },
                { timeText: "11:00 AM - 12:00 PM", startTimeString: "11:00:00", endTimeString: "12:00:00" },
                { timeText: "12:00 PM - 1:00 PM",  startTimeString: "12:00:00", endTimeString: "13:00:00" },
                { timeText: "1:00 PM - 2:00 PM",   startTimeString: "13:00:00", endTimeString: "14:00:00" },
                { timeText: "2:00 PM - 3:00 PM",   startTimeString: "14:00:00", endTimeString: "15:00:00" },
                { timeText: "3:00 PM - 4:00 PM",   startTimeString: "15:00:00", endTimeString: "16:00:00" },
                { timeText: "4:00 PM - 5:00 PM",   startTimeString: "16:00:00", endTimeString: "17:00:00" },
                { timeText: "5:00 PM - 6:00 PM",   startTimeString: "17:00:00", endTimeString: "18:00:00" },
                { timeText: "6:00 PM - 7:00 PM",   startTimeString: "18:00:00", endTimeString: "19:00:00" },
                { timeText: "7:00 PM - 8:00 PM",   startTimeString: "19:00:00", endTimeString: "20:00:00" },
                { timeText: "8:00 PM - 9:00 PM",   startTimeString: "20:00:00", endTimeString: "21:00:00" },
                { timeText: "9:00 PM - 10:00 PM",  startTimeString: "21:00:00", endTimeString: "22:00:00" },
                { timeText: "10:00 PM - 11:00 PM", startTimeString: "22:00:00", endTimeString: "23:00:00" },
                { timeText: "11:00 PM - 0:00 AM",  startTimeString: "23:00:00", endTimeString: "00:00:00" },
            ],
            assignMonthSelection: [
                { monthText: "January",   monthString: "01" },
                { monthText: "Febuary",   monthString: "02" },
                { monthText: "March",     monthString: "03" },
                { monthText: "April",     monthString: "04" },
                { monthText: "May",       monthString: "05" },
                { monthText: "June",      monthString: "06" },
                { monthText: "July",      monthString: "07" },
                { monthText: "August",    monthString: "08" },
                { monthText: "September", monthString: "09" },
                { monthText: "October",   monthString: "10" },
                { monthText: "November",  monthString: "11" },
                { monthText: "December",  monthString: "12" },
            ],
                        responseList: ref([]),
            collapses: [],
            showAddedToast: false,
            addedToastTimer: null
        }
    },
    computed: {
        // Client-side search over the full Firestore-filtered list
        searchFilteredList() {
            if (!this.searchTerm.trim()) return this.allTesterList;
            const term = this.searchTerm.toLowerCase();
            return this.allTesterList.filter(t =>
                (t.name  || '').toLowerCase().includes(term) ||
                (t.email || '').toLowerCase().includes(term)
            );
        },
        // Slice for the current page
        displayedTesters() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.searchFilteredList.slice(start, start + this.pageSize);
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.searchFilteredList.length / this.pageSize));
        }
    },
    watch: {
        // Reset to page 1 whenever the displayed list changes
        searchFilteredList() { this.currentPage = 1; }
    },
    mounted() {
        if (window.localStorage.getItem("userType")) {
            this.userType = parseInt(window.localStorage.getItem("userType"));
        } else {
            this.$router.push({ name: 'login' });
        }
        this.getFilters();
        this.getLatestTesterList();

        let curYear = new Date().getFullYear();
        for (let i = 0; i < 10; i++) {
            this.assignYearSelection.push({ yearText: (curYear + i).toString() });
        }
        this.assignYearSelected  = today.getFullYear();
        this.assignMonthSelected = "01";

        let totalDays = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
        for (let i = 1; i <= totalDays; i++) {
            this.assignDaySelection.push({ dayText: i < 10 ? "0" + i : i.toString() });
        }
        this.assignDaySelected = "01";

        if (this.userType === 1) {
            this.$nextTick(() => {
                initializePageOnboarding('testerList', this.userType, onboardingService, this.$store);
            });
        }
    },
    methods: {

        // ── Filter methods ────────────────────────────────────────────

        // Turn camelCase field names into display labels (e.g. purchasingBehaviour -> Purchasing Behaviour)
        formatFieldName(fieldName) {
            return fieldName
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase());
        },

        handleFieldValue(fieldValue, field_index) {
            if (this.selectedTags.find(t => t.fieldValue === fieldValue)) return;
            const target = this.testerFields[field_index].fieldValues.find(v => v.value === fieldValue);
            if (target) target.isAdded = true;
            this.selectedTags.push({ fieldValue, field_index });
            this.fetchAllTesters();
        },

        removeTag(tag) {
            const target = this.testerFields[tag.field_index].fieldValues.find(v => v.value === tag.fieldValue);
            if (target) target.isAdded = false;
            this.selectedTags = this.selectedTags.filter(t => t.fieldValue !== tag.fieldValue);
            this.fetchAllTesters();
        },

        handleClear() {
            this.selectedTags.forEach(tag => {
                const target = this.testerFields[tag.field_index].fieldValues.find(v => v.value === tag.fieldValue);
                if (target) target.isAdded = false;
            });
            this.selectedTags = [];
            this.searchTerm   = "";
            this.fetchAllTesters();
        },

        // Debounce search so we don't re-filter on every keystroke
        onSearchInput() {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => { this.currentPage = 1; }, 300);
        },

        // ── Core: fetch ALL matching testers in one go ────────────────
        // We pass a huge pageSize (9999) so Firestore returns everything,
        // then paginate entirely on the client. This means filters always
        // apply across the complete tester pool, not just the visible page.
        fetchAllTesters() {
            if (!this.testerFields.length) return;
            this.isLoadingTesters = true;
            this.currentPage = 1;

            const fireStoreFilter = this.testerFields.map(field => ({
                fieldName: field.fieldName,
                fieldValues: []
            }));
            this.selectedTags.forEach(tag => {
                fireStoreFilter[tag.field_index].fieldValues.push(tag.fieldValue);
            });

            // pageNum=1, pageSize=9999 → returns the full filtered list at once
            FilteringTesters.getTester(this.gameId, 1, 9999, fireStoreFilter)
                .then(res => {
                    this.allTesterList    = res.data.testerList;
                    this.testerError      = "";
                    this.isLoadingTesters = false;
                })
                .catch(error => {
                    console.log(error);
                    this.testerError      = (error && error.error) || "Testers could not be loaded.";
                    this.isLoadingTesters = false;
                });
        },

        // ── Other Firestore methods ───────────────────────────────────

        getFilters() {
            this.isLoading = true;
            FilteringTesters.getFilter()
                .then(res => {
                    this.testerFields = res.data.testerFields;
                    this.variantsList = res.data.variantsList;
                    this.isLoading    = false;
                    // Load testers after filters are ready
                    this.fetchAllTesters();
                })
                .catch(error => { console.log(error); this.isLoading = false; });
        },

        getLatestTesterList() {
            this.isLoading = true;
            GamesObject.getProject(this.gameId)
                .then(res => { this.latestTesterIDs = res.data.testerIds; this.isLoading = false; })
                .catch(error => { console.log(error); this.isLoading = false; });
        },

        handleAddTester(testerData) {
            if (testerData && testerData.id) {
                this.$set(this.optimisticTesterData, testerData.id, testerData);
                if (!this.latestTesterIDs.includes(testerData.id)) {
                    this.latestTesterIDs.push(testerData.id);
                }
            }
            this.fetchAllTesters();
            this.getLatestTesterList();

            this.showAddedToast = true;
            clearTimeout(this.addedToastTimer);
                this.addedToastTimer = setTimeout(() => {
                this.showAddedToast = false;
            }, 2500);
        },
        toDetailTest()     { this.getLatestTesterList(); this.current = 0; },

        handleGoResponse(testerId) {
            this.assignTesterId = testerId;
            this.isLoading = true;
            SurveyAnalytics.getResponseListbyTesterProject(this.assignTesterId, this.gameId)
                .then(res => {
                    this.$set(this, "responseList", res.data);
                    this.current   = 1;
                    this.collapses = this.initializeCollapse();
                    this.isLoading = false;
                })
                .catch(error => { console.log(error); this.isLoading = false; });
        },

        handleEditTimeSlot(testerId, timeSlotList) {
            this.assignTesterId           = testerId;
            this.assigntesterTimeSlotList = timeSlotList;
            this.current = 3;
            this.getTesterInfo();
        },

        handleRemoveTester(testerId) {
            if (!window.confirm("Do you really want to remove this tester? All information will be lost forever!")) return;
            this.isLoading = true;
            GameTesting.deleteTester(this.gameId, testerId)
                .then(() => {
                    this.$delete(this.optimisticTesterData, testerId);
                    this.isLoading = false;
                    this.getLatestTesterList();
                })
                .catch(error => { console.log(error); this.isLoading = false; });
        },

        getTesterInfo() {
            this.isLoading = true;
            UserObject.getProfile(this.assignTesterId)
                .then(res => { this.assigntesterInfo = res.data; this.isLoading = false; })
                .catch(error => { console.log(error); this.isLoading = false; });
        },

        initializeCollapse() {
            this.collapses = [];
            for (let i = 0; i < this.responseList.length; i++) this.collapses.push({ show: false });
            return this.collapses;
        },
        openAll()  { this.collapses.forEach(c => { c.show = true;  }); },
        closeAll() { this.collapses.forEach(c => { c.show = false; }); },

        handleSelect(box, event) {
            const idx = event.target.selectedIndex;
            if (box === "year") {
                this.assignYearSelected = this.assignYearSelection[idx].yearText;
                if (this.assignMonthSelected) {
                    let total = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                    this.assignDaySelection = [];
                    for (let i = 1; i <= total; i++) this.assignDaySelection.push({ dayText: i < 10 ? "0"+i : ""+i });
                }
            } else if (box === "month") {
                this.assignMonthSelected = this.assignMonthSelection[idx].monthString;
                if (this.assignYearSelected) {
                    let total = new Date(parseInt(this.assignYearSelected), parseInt(this.assignMonthSelected), 0).getDate();
                    this.assignDaySelection = [];
                    for (let i = 1; i <= total; i++) this.assignDaySelection.push({ dayText: i < 10 ? "0"+i : ""+i });
                }
            } else if (box === "day") {
                this.assignDaySelected = this.assignDaySelection[idx].dayText;
            } else {
                this.assignHourSelected = idx;
            }
        },

        handleAdd() {
            if (!this.assignTesterId) { alert('Pick a playtester first.'); return; }
            const s = this.assignHourSelection[this.assignHourSelected];
            const start = `${this.assignYearSelected}-${this.assignMonthSelected}-${this.assignDaySelected}T${s.startTimeString}`;
            const end   = `${this.assignYearSelected}-${this.assignMonthSelected}-${this.assignDaySelected}T${s.endTimeString}`;
            this.isLoading = true;
            GameTesting.assignTimeSlot(this.gameId, this.assignTesterId, [start, end])
                .then(() => { this.isLoading = false; })
                .catch(error => { console.log(error); this.isLoading = false; });
        },

        async handleMessageTester(testerId) {
            try {
                this.isLoading = true;
                const developerId = window.localStorage.getItem('userId');
                if (!developerId) { alert('User not logged in.'); this.isLoading = false; return; }

                const convResult = await Chat.getOrCreateConversation(testerId, developerId);
                if (!convResult.success) { alert('Failed to open chat.'); this.isLoading = false; return; }

                const userResult = await Chat.getUserInfo(testerId);
                if (!userResult.success) { alert('Failed to load tester info.'); this.isLoading = false; return; }

                const contact = {
                    id: testerId,
                    conversationId: convResult.data.conversationId,
                    name: userResult.data.nickName || userResult.data.userName || 'Tester',
                    avatar: userResult.data.img || require('../../assets/avator_example.jpg'),
                    lastMessage: convResult.data.lastMessage || 'Start a conversation',
                    lastMessageAt: convResult.data.lastMessageAt || new Date(),
                    unreadCount: convResult.data.unreadCount || {},
                    messages: []
                };

                const idx = this.messageContacts.findIndex(c => c.id === testerId);
                if (idx >= 0) this.$set(this.messageContacts, idx, contact);
                else this.messageContacts.push(contact);

                this.isLoading = false;
                this.$nextTick(() => {
                    if (this.$refs.messagePanel) this.$refs.messagePanel.openChatWithContact(contact);
                });
                } catch (error) {
                console.error(error);
                this.isLoading = false;
            }
        },
    }
}
</script>

<style scoped>

/* Scrollbar styles inherited from global App.vue */

/* ── Page sections ── */
.testers-view0 {
    display: flex;
    flex-direction: column;
    height: 75vh;
}

#title-wrapper { 
    margin-right: 5%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 20px;
    flex-shrink: 0;
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    padding: 20px 28px;
}
#game-detail-testers { margin-right: 0; height: 100%; }
#game-detail-testers-response,
#game-detail-testers-assign { margin-right: 5%; }

.addtester-back {
    display: inline-flex;
    align-items: center;
    background-color: #FC7727;
    color: white;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: bold;
    font-size: 14px;
    cursor: pointer;
    margin-bottom: 16px;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    width: fit-content;
    margin-left: 20px;
}
.addtester-back:hover { background-color: #ff8e4d; transform: translateY(-1px); }

/* ── Merged Testers Dashboard card ── */
.testers-card {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    margin-right: 5%;
    margin-top: 10px;
    flex: 1 1 auto;
    min-height: 0;
}

.testers-card-section { padding: 4px 0; }

.testers-panel-yours {
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    padding: 24px 32px 24px 28px;
    width: 320px;
    flex-shrink: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
}

.testers-panel-find {
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    padding: 24px 32px 24px 28px;
    flex: 1;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
}

.testers-card-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    flex-wrap: wrap;
    gap: 8px;
}

.testers-card-title {
    font-weight: 700;
    color: #ff7222;
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
}

.testers-count-badge {
    background: #ff7222;
    color: white;
    font-size: 12px;
    font-weight: 700;
    border-radius: 12px;
    padding: 2px 10px;
    min-width: 22px;
    text-align: center;
}

.testers-panel-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 8px;
    margin-right: -8px;
}

.testers-empty-hint {
    font-size: 13px;
    color: #999;
    padding: 8px 0 4px;
}

/* View 0 tester grid (assigned testers) */
.tester-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* ── Find / add testers controls ── */
.find-testers-controls {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
}

.filter-search-input.form-control {
    background: #f0f0f0 !important;
    border-radius: 10px !important;
    border: none !important;
    padding: 0.5rem 0.9rem;
    font-size: 13px;
    flex: 1;
}

.filters-toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f6f6f6;
    border: 1px solid #ddd;
    border-radius: 10px;
    padding: 0.5rem 0.9rem;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
}
.filters-toggle-btn:hover { border-color: #ff7222; color: #ff7222; }

.filters-count { color: #ff7222; }

.filters-caret { font-size: 0.5rem; transition: transform 0.2s; display: inline-block; }
.filters-caret.open { transform: rotate(180deg); }

.filters-drawer {
    background: #fafafa;
    border-radius: 10px;
    padding: 10px 14px 2px;
    margin-bottom: 14px;
}

/* Active filter pills */
.selected-tags-wrapper { margin-bottom: 12px; }

.selected-tags-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-bottom: 8px;
}

.tag-button-chosen {
    background: #db3360;
    border: none;
    color: white;
    border-radius: 20px;
    font-size: 11px;
    padding: 3px 10px;
    cursor: pointer;
    transition: background 0.2s;
}
.tag-button-chosen:hover { background: #c22c54; }

.filter-clear-btn {
    background: none;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 11px;
    color: #666;
    padding: 3px 10px;
    cursor: pointer;
    transition: all 0.2s;
}
.filter-clear-btn:hover { border-color: #ff7222; color: #ff7222; }

/* Collapsible field sections */
.tag-dropdown {
    border-bottom: 1px solid #eee;
    margin-bottom: 4px;
}
.tag-dropdown:last-child { border-bottom: none; }

.tag-category-header {
    font-weight: 600;
    cursor: pointer;
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 13px;
    color: #333;
    user-select: none;
}
.tag-category-header::-webkit-details-marker { display: none; }
.tag-category-header::after { content: '▲'; font-size: 0.55rem; color: #ff7222; transition: transform 0.2s; }
.tag-dropdown[open] .tag-category-header::after { transform: rotate(180deg); }

.tag-options {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    padding: 4px 0 10px;
}

.tag-btn {
    padding: 3px 10px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 20px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s;
    color: #555;
}
.tag-btn:hover   { border-color: #ff7222; color: #ff7222; }
.tag-btn.selected { background: #ff7222; color: white; border-color: #ff7222; }

.results-count {
    font-size: 13px;
    color: #888;
    font-weight: 600;
}

.loading-testers {
    text-align: center;
    color: #aaa;
    padding: 40px 0;
    font-size: 14px;
}

.tester-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
    padding-bottom: 8px;
}

/* ── Pagination ── */
.pagination-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 12px 0 4px;
}

.page-btn {
    background: #ff7222;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
}

/* ── Added Tester Toast ── */
.added-toast {
    position: fixed;
    bottom: 13px;
    right: 24px;
    background: #ffffff;
    border-left: 4px solid #4caf50;
    color: #333;
    padding: 14px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    z-index: 999;
}

.added-toast-icon {
    color: #4caf50;
    font-weight: bold;
    font-size: 16px;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.toast-fade-enter,
.toast-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}


.page-btn:hover:not(:disabled) { background: #ff8e4d; }
.page-btn:disabled { background: #ddd; color: #999; cursor: not-allowed; }

.page-info { font-size: 13px; color: #555; font-weight: 600; }

/* ── No results ── */
.add-tester-no-tester { width: 100%; text-align: center; padding: 40px 0; }
.add-tester-no-tester-img { height: 80px; margin-bottom: 12px; }
.add-tester-no-tester-txt { font-size: 14px; color: #888; }

/* ── Responses / assign ── */
.white-text { color: #000; }
.no-response { width: 100%; margin-top: 50px; }
.no-response-img { display: block; height: 100px; margin: 0 auto 12px; }
.no-response-txt { text-align: center; font-size: 16px; color: #383737; }

.game-detail-testers-add-title {
    margin-top: 14px; height: 46px;
    font-weight: bold; font-size: 18px; line-height: 46px; color: #C4C2C2;
}

.game-detail-assign-ctn { position: relative; display: flex; width: 100%; flex-wrap: wrap; gap: 8px; }
.game-detail-assign-date-item { height: 42px; font-size: 15px; line-height: 42px; color: #414040; display: flex; align-items: center; gap: 8px; }
.game-detail-assign-btn {
    width: 75px; height: 34px;
    background: rgba(229,83,66,0.7); border: none;
    border-radius: 6px; font-weight: bold; font-size: 12px;
    color: #fff; cursor: pointer; align-self: center;
}
.game-detail-assign-selection-box {
    height: 28px; border: 1px solid #C4C4C4;
    background: #36393F; font-size: 11px;
    color: #fff; padding: 0 4px;
}

select:focus { border: 2px rgba(229,83,66,0.6) solid !important; }

.response-title { width: 100%; text-align: left !important; }
.response-title:focus { outline: none; box-shadow: none; }
.response-detail { background-color: transparent !important; border: 1px solid #ddd !important; }
.all { background-color: transparent !important; color: #3c3c3d !important; }
.all:hover { color: #975952 !important; }
.active-style { background-color: #E9E9EA !important; color: #666464 !important; }
.hidden-style { background-color: transparent !important; color: #666464 !important; }
.response-text { margin-bottom: 2rem; }
</style>


// WEBPACK FOOTER //
// src/components/game-detail/GameDetail-Testers.vue